// src/components/FloatingNewsWidget.tsx - Updated with modular color system
'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Card,
  CardContent,
  Chip,
  Button,
  Skeleton,
  Badge,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Article as ArticleIcon,
  Close as CloseIcon,
  CalendarToday,
  OpenInNew,
} from '@mui/icons-material';

// Import centralized types and hooks
import type {
  FloatingNewsWidgetProps,
  NewsArticle,
  NewsArticleClickHandler,
  StyledComponentProps,
} from '@/types';
import { useLatestNews, useLocalStorage } from '@/hooks/useApi';
import { formatDate } from '@/lib/api';
import { useRouter } from 'next/navigation';

// Import our modular color system
import { withOpacity } from '@/lib/colors';
import { useColorTheme } from '@/components/ColorModeProvider';

// =====================
// STYLED COMPONENTS
// =====================

const FloatingContainer = styled(Paper, {
  shouldForwardProp: (prop) => !['isExpanded', 'isMobile'].includes(prop as string),
})<Pick<StyledComponentProps, 'isExpanded' | 'isMobile'>>(({ theme, isExpanded, isMobile }) => {
  const colors = theme.colors;
  return {
    position: 'fixed',
    top: isMobile ? '80px' : '50%',
    right: '20px',
    transform: isMobile ? 'none' : 'translateY(-50%)',
    width: isExpanded ? (isMobile ? '90vw' : '380px') : '60px',
    maxHeight: isExpanded ? (isMobile ? '80vh' : '70vh') : '60px',
    zIndex: 1200,
    background: colors.gradients.card,
    border: `1px solid ${colors.brand.accent.main}`,
    borderRadius: theme.spacing(2),
    transition: 'all 0.3s ease',
    overflow: 'hidden',
    boxShadow: `0 8px 32px ${withOpacity(colors.background.default, 0.4)}`,
    
    '&:hover': {
      boxShadow: `0 12px 40px ${colors.effects.glow}`,
    },
  };
});

const NewsHeader = styled(Box)(({ theme }) => {
  const colors = theme.colors;
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px', // theme.spacing(2)
    borderBottom: `1px solid ${withOpacity(colors.brand.accent.main, 0.2)}`,
  };
});

const NewsItem = styled(Card)(({ theme }) => {
  const colors = theme.colors;
  return {
    background: withOpacity(colors.text.primary, 0.02),
    border: `1px solid ${withOpacity(colors.text.primary, 0.1)}`,
    marginBottom: '8px', // theme.spacing(1)
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    
    '&:hover': {
      background: withOpacity(colors.brand.accent.main, 0.05),
      borderColor: withOpacity(colors.brand.accent.main, 0.3),
      transform: 'translateX(-2px)',
    },
    
    '&:last-child': {
      marginBottom: 0,
    },
  };
});

const NewsTooltip = styled(Paper, {
  shouldForwardProp: (prop) => !['show', 'isMobile'].includes(prop as string),
})<Pick<StyledComponentProps, 'show' | 'isMobile'>>(({ theme, show, isMobile }) => {
  const colors = theme.colors;
  return {
    position: 'fixed',
    right: isMobile ? '95px' : '90px',
    top: isMobile ? '70px' : '50%',
    // Combined transform - handle both positioning and show/hide animations
    transform: show 
      ? (isMobile ? 'translateX(0) scale(1)' : 'translateY(-50%) translateX(0) scale(1)')
      : (isMobile ? 'translateX(10px) scale(0.9)' : 'translateY(-50%) translateX(10px) scale(0.9)'),
    background: colors.gradients.primary,
    color: colors.brand.accent.contrastText,
    padding: '12px 16px', // theme.spacing(1.5, 2)
    borderRadius: '8px', // theme.spacing(1)
    fontSize: '0.85rem',
    fontWeight: 600,
    maxWidth: isMobile ? '200px' : '240px',
    boxShadow: `0 4px 24px ${colors.effects.shadow}`,
    opacity: show ? 1 : 0,
    visibility: show ? 'visible' : 'hidden',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 9999,
    whiteSpace: 'normal',
    pointerEvents: 'none',
    
    '&::after': {
      content: '""',
      position: 'absolute',
      right: '-8px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 0,
      height: 0,
      borderLeft: `8px solid ${colors.brand.accent.main}`,
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
    },
    
    animation: show ? 'tooltipPulse 2s ease-in-out' : 'none',
    
    '@keyframes tooltipPulse': {
      '0%, 100%': {
        boxShadow: `0 8px 24px ${colors.effects.shadow}`,
      },
      '50%': {
        boxShadow: `0 12px 32px ${colors.effects.glow}`,
      },
    },
  };
});

const CollapsedButton = styled(IconButton)(({ theme }) => {
  const colors = theme.colors;
  return {
    width: '100%',
    height: '100%',
    borderRadius: '16px', // theme.spacing(2)
    color: colors.brand.accent.dark,
    
    '&:hover': {
      background: withOpacity(colors.brand.accent.main, 0.1),
    },
  };
});

// =====================
// SUB-COMPONENTS
// =====================

interface NewsItemComponentProps {
  article: NewsArticle;
  onClick: NewsArticleClickHandler;
}

const NewsItemComponent = React.memo<NewsItemComponentProps>(({ article, onClick }) => {
  // Get colors inside component for runtime access
  const colors = useColorTheme();
  
  const handleClick = useCallback(() => {
    onClick(article);
  }, [article, onClick]);

  return (
    <NewsItem onClick={handleClick}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Chip
            label={article.category}
            size="small"
            sx={{
              background: withOpacity(colors.brand.accent.main, 0.2),
              color: colors.brand.accent.dark,
              fontSize: '0.7rem',
              height: '20px',
            }}
          />
          {article.externalLink && (
            <OpenInNew sx={{ fontSize: '0.9rem', color: colors.text.disabled }} />
          )}
        </Box>
        
        <Typography
          variant="body2"
          sx={{
            color: colors.text.secondary,
            fontWeight: 500,
            fontSize: '0.85rem',
            lineHeight: 1.3,
            mb: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {article.title}
        </Typography>
        
        {article.excerpt && (
          <Typography
            variant="caption"
            sx={{
              color: colors.text.disabled,
              fontSize: '0.75rem',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mb: 1,
            }}
          >
            {article.excerpt}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CalendarToday sx={{ fontSize: '0.7rem', color: colors.text.disabled }} />
          <Typography variant="caption" sx={{ color: colors.text.disabled, fontSize: '0.7rem' }}>
            {formatDate(article.publishedDate)}
          </Typography>
        </Box>
      </CardContent>
    </NewsItem>
  );
});

NewsItemComponent.displayName = 'NewsItemComponent';

const LoadingSkeleton = React.memo(() => {
  // Get colors inside component for runtime access
  const colors = useColorTheme();
  
  return (
    <>
      {Array.from({ length: 3 }, (_, index) => (
        <Card key={index} sx={{ background: withOpacity(colors.text.primary, 0.02), mb: 1 }}>
          <CardContent sx={{ p: 2 }}>
            <Skeleton 
              variant="text" 
              height={20} 
              width="60%" 
              sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} 
            />
            <Skeleton 
              variant="text" 
              height={16} 
              sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), mt: 1 }} 
            />
            <Skeleton 
              variant="text" 
              height={16} 
              width="80%" 
              sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} 
            />
          </CardContent>
        </Card>
      ))}
    </>
  );
});

LoadingSkeleton.displayName = 'LoadingSkeleton';

// =====================
// MAIN COMPONENT
// =====================

const FloatingNewsWidget: React.FC<FloatingNewsWidgetProps> = ({
  maxItems = 5,
  autoRefresh = false,
  refreshInterval = 30,
  showWelcomeTooltip = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const router = useRouter();

  // Get colors inside component for runtime access
  const colors = useColorTheme();

  // Component state
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipDismissed, setTooltipDismissed] = useState(false);
  const [hoverTooltipOpen, setHoverTooltipOpen] = useState(false);
  
  // Local storage for tracking read articles
  const [lastReadTime, setLastReadTime] = useLocalStorage<string>('lastNewsReadTime', '');
  
  // API data
  const { articles: newsArticles, loading, error, refetch } = useLatestNews(maxItems);

  // Calculate unread count
  const unreadCount = useMemo(() => {
    if (!lastReadTime || newsArticles.length === 0) {
      return newsArticles.length > 0 ? 1 : 0;
    }
    
    const lastRead = new Date(lastReadTime);
    return newsArticles.filter(article => 
      new Date(article.publishedDate) > lastRead
    ).length;
  }, [newsArticles, lastReadTime]);

  // Handle auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const refreshTimer = setInterval(() => {
      refetch();
    }, refreshInterval * 60 * 1000);

    return () => clearInterval(refreshTimer);
  }, [autoRefresh, refreshInterval, refetch]);

  // Handle tooltip show/hide logic
  useEffect(() => {
    if (!showWelcomeTooltip || tooltipDismissed || isExpanded) {
      return;
    }

    let showTimer: NodeJS.Timeout;
    let hideTimer: NodeJS.Timeout;

    showTimer = setTimeout(() => {
      if (!isExpanded && !tooltipDismissed) {
        setShowTooltip(true);
        
        hideTimer = setTimeout(() => {
          setShowTooltip(false);
          setTooltipDismissed(true);
        }, 4000);
      }
    }, 2000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [showWelcomeTooltip, tooltipDismissed, isExpanded]);

  // Hide tooltip when expanded state changes
  useEffect(() => {
    if (isExpanded) {
      setShowTooltip(false);
    }
  }, [isExpanded]);

  // Event handlers
  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
    setShowTooltip(false);
    setTooltipDismissed(true);
    
    if (!isExpanded) {
      setLastReadTime(new Date().toISOString());
    }
  }, [isExpanded, setLastReadTime]);

  const handleArticleClick = useCallback<NewsArticleClickHandler>((article) => {
    if (article.externalLink) {
      window.open(article.externalLink, '_blank', 'noopener,noreferrer');
    } else {
      setIsExpanded(false);
      router.push(`/news/${article.documentId}`);
    }
  }, [router]);

  const handleViewAllNews = useCallback(() => {
    setIsExpanded(false); // Close the widget
    router.push('/news-events');
  }, [router]);

  // Memoized content rendering
  const newsContent = useMemo(() => {
    if (loading) {
      return <LoadingSkeleton />;
    }
    
    if (error) {
      return (
        <Typography variant="body2" sx={{ color: colors.text.secondary, textAlign: 'center', p: 2 }}>
          {error}
        </Typography>
      );
    }
    
    if (newsArticles.length === 0) {
      return (
        <Typography variant="body2" sx={{ color: colors.text.disabled, textAlign: 'center', p: 2 }}>
          No news articles available
        </Typography>
      );
    }
    
    return newsArticles.map((article) => (
      <NewsItemComponent 
        key={article.id} 
        article={article} 
        onClick={handleArticleClick} 
      />
    ));
  }, [loading, error, newsArticles, handleArticleClick, colors.text.secondary, colors.text.disabled]);

  return (
    <>
      {/* Tooltip rendered outside container to prevent z-index issues */}
      {!isExpanded && (
        <NewsTooltip show={showTooltip} isMobile={isMobile}>
          Check out our latest news here!
        </NewsTooltip>
      )}
      
      <FloatingContainer isExpanded={isExpanded} isMobile={isMobile}>
        {!isExpanded ? (
          // Collapsed state
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <Tooltip 
              title="Latest News" 
              placement="left"
              open={hoverTooltipOpen && !showTooltip && !tooltipDismissed}
              onClose={() => setHoverTooltipOpen(false)}
              onOpen={() => setHoverTooltipOpen(true)}
            >
              <Badge badgeContent={unreadCount} color="error">
                <CollapsedButton 
                  onClick={handleToggle}
                  onMouseEnter={() => setHoverTooltipOpen(true)}
                  onMouseLeave={() => setHoverTooltipOpen(false)}
                >
                  <ArticleIcon fontSize="large" />
                </CollapsedButton>
              </Badge>
            </Tooltip>
          </Box>
        ) : (
          // Expanded state
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <NewsHeader>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ArticleIcon sx={{ color: colors.brand.accent.dark }} />
                <Typography variant="h6" sx={{ color: colors.brand.accent.dark, fontWeight: 600 }}>
                  Latest News
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={handleToggle}
                sx={{ 
                  color: colors.text.secondary,
                  '&:hover': {
                    color: colors.brand.accent.dark,
                    background: withOpacity(colors.brand.accent.main, 0.1),
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </NewsHeader>

            <Box sx={{ 
              flexGrow: 1, 
              overflowY: 'auto', 
              p: 2,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: withOpacity(colors.text.primary, 0.1),
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: withOpacity(colors.brand.accent.main, 0.5),
                borderRadius: '3px',
              },
            }}>
              {newsContent}
            </Box>

            <Box sx={{ p: 2, borderTop: `1px solid ${withOpacity(colors.brand.accent.main, 0.2)}` }}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={handleViewAllNews}
                sx={{
                  color: colors.brand.accent.dark,
                  borderColor: colors.brand.accent.main,
                  fontSize: '0.8rem',
                  '&:hover': {
                    background: withOpacity(colors.brand.accent.main, 0.1),
                    borderColor: colors.brand.accent.main,
                  }
                }}
              >
                View All News
              </Button>
            </Box>
          </Box>
        )}
      </FloatingContainer>
    </>
  );
};

export default React.memo(FloatingNewsWidget);